import { useState, useEffect, useRef } from 'react';
import { addRendition, RenditionSize } from '@shared/utils/photoRendition';

interface UseProgressiveImageUrlProps {
  photoUrl: string;
  lowResRendition?: RenditionSize;
  highResRendition?: RenditionSize;
}

interface ProgressiveImageState {
  lowResUrl: string;
  highResUrl: string;
  isHighResLoaded: boolean;
}

export const useProgressiveImageUrl = ({ photoUrl, lowResRendition = 'medium', highResRendition = 'full' }: UseProgressiveImageUrlProps): ProgressiveImageState => {
  const [state, setState] = useState<ProgressiveImageState>({
    lowResUrl: addRendition({ url: photoUrl, renditionSize: lowResRendition }),
    highResUrl: addRendition({ url: photoUrl, renditionSize: highResRendition }),
    isHighResLoaded: false,
  });

  const highResImageRef = useRef<HTMLImageElement | null>(null);

  // Load high-resolution image in background
  useEffect(() => {
    if (!state.highResUrl) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      setState(prev => ({ ...prev, isHighResLoaded: true }));
    };

    img.src = state.highResUrl;
    highResImageRef.current = img;

    return () => {
      if (highResImageRef.current) {
        highResImageRef.current.onload = null;
        highResImageRef.current.onerror = null;
        highResImageRef.current = null;
      }
    };
  }, [state.highResUrl]);

  // Update URLs when photoUrl changes
  useEffect(() => {
    setState({
      lowResUrl: addRendition({ url: photoUrl, renditionSize: lowResRendition }),
      highResUrl: addRendition({ url: photoUrl, renditionSize: highResRendition }),
      isHighResLoaded: false,
    });
  }, [photoUrl, lowResRendition, highResRendition]);

  return state;
};
